'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  Share2,
  Link as LinkIcon,
  List,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import { FaTwitter, FaLinkedin, FaGithub, FaInstagram } from 'react-icons/fa';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

type ApiBlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage?: string | null;
  author: string;
  category: string;
  tags: string[];
  readTime: number;
  publishedAt?: string | null;
  createdAt: string;
};

interface BlogSection {
  heading: string;
  html: string;
}

// Parse a post's HTML/plain content into top-level sections keyed by h2 headings.
// Falls back to a single section when no headings are present.
function parseSections(content: string): BlogSection[] {
  if (!content) return [];
  const normalized = content.replace(/\r\n/g, '\n');

  const h2Regex = /<h2[^>]*>([\s\S]*?)<\/h2>|^##\s+(.+)$/gim;
  const matches: Array<{ heading: string; index: number; matchLen: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = h2Regex.exec(normalized)) !== null) {
    const heading = (m[1] ?? m[2] ?? '').replace(/<[^>]+>/g, '').trim();
    matches.push({ heading, index: m.index, matchLen: m[0].length });
  }

  if (matches.length === 0) {
    return [{ heading: 'Article', html: normalized }];
  }

  const sections: BlogSection[] = [];
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].index + matches[i].matchLen;
    const end = i + 1 < matches.length ? matches[i + 1].index : normalized.length;
    const body = normalized.slice(start, end).trim();
    sections.push({ heading: matches[i].heading || `Section ${i + 1}`, html: body });
  }
  return sections;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const GRADIENTS = [
  'linear-gradient(135deg, #6C63FF 0%, #FF6584 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #FF6584 100%)',
  'linear-gradient(135deg, #06b6d4 0%, #6C63FF 100%)',
  'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
  'linear-gradient(135deg, #22c55e 0%, #06b6d4 100%)',
];

export default function BlogPostPage() {
  const params = useParams();
  const slug = (params?.slug as string | undefined) ?? '';

  const post = useSiteData<ApiBlogPost | null>(
    `blog-post:${slug}`,
    `/api/blog/slug/${slug}`,
    null,
  );
  const allPosts = useSiteData<ApiBlogPost[]>('blog', '/api/blog', []);

  const [activeSection, setActiveSection] = useState(0);
  const [readProgress, setReadProgress] = useState(0);
  const sectionRefs = useRef<(HTMLHeadingElement | null)[]>([]);

  const sections = useMemo(() => parseSections(post?.content ?? ''), [post?.content]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(Math.min(100, Math.max(0, (scrollTop / docH) * 100)));

      const scrollY = window.scrollY + 150;
      let current = 0;
      sectionRefs.current.forEach((ref, i) => {
        if (ref && ref.offsetTop <= scrollY) current = i;
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post, sections.length]);

  const scrollToSection = (idx: number) => {
    const el = sectionRefs.current[idx];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: 'smooth' });
  };

  const copyLink = () => {
    if (typeof navigator !== 'undefined') {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (!post) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 className="blog-article-title">Post Not Found</h1>
          <Link href="/blog" className="blog-detail-back" style={{ marginTop: 20 }}>
            <ArrowLeft size={14} /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const tags = (post.tags as string[]) ?? [];
  const relatedPosts = allPosts.filter((p) => p.slug !== slug).slice(0, 2);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const twitterShare = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`;
  const linkedinShare = `https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const gradient =
    GRADIENTS[Math.max(0, allPosts.findIndex((p) => p.slug === slug)) % GRADIENTS.length];

  return (
    <div className="blog-detail-page">
      <div className="blog-detail-wrap">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Link href="/blog" className="blog-detail-back">
            <ArrowLeft size={14} /> Back to Blog
          </Link>
        </motion.div>

        <div className="blog-detail-grid">
          {/* LEFT: Table of Contents (sticky) */}
          <aside className="blog-toc-col">
            <div className="blog-toc">
              <div className="blog-toc-title">
                <List size={12} />
                <span>Table of Contents</span>
              </div>
              <ul className="blog-toc-list">
                {sections.map((section, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(i)}
                      className={`blog-toc-item ${activeSection === i ? 'blog-toc-item-active' : ''}`}
                    >
                      <span className="blog-toc-num">{i + 1}</span>
                      <span>{section.heading}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="blog-toc-progress">
                <div className="blog-toc-progress-label">
                  <span>Reading progress</span>
                  <span>{Math.round(readProgress)}%</span>
                </div>
                <div className="blog-toc-progress-bar">
                  <div className="blog-toc-progress-fill" style={{ width: `${readProgress}%` }} />
                </div>
              </div>
            </div>
          </aside>

          {/* CENTER: Main Article */}
          <article className="blog-main-col">
            <motion.div
              className="blog-article-header"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="blog-article-category">
                <Sparkles size={11} /> {post.category}
              </span>
              <h1 className="blog-article-title">{post.title}</h1>
              <div className="blog-article-meta">
                <span className="blog-article-meta-item">
                  <Calendar size={13} />
                  {formatDate(post.publishedAt || post.createdAt)}
                </span>
                <span className="blog-article-meta-item">
                  <Clock size={13} />
                  {post.readTime} min read
                </span>
                {sections.length > 0 && (
                  <span className="blog-article-meta-item">
                    <BookOpen size={13} />
                    {sections.length} {sections.length === 1 ? 'section' : 'sections'}
                  </span>
                )}
              </div>
            </motion.div>

            <motion.div
              className="blog-article-cover"
              style={{
                background: gradient,
                position: 'relative',
                overflow: 'hidden',
              }}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {post.coverImage && (
                <OptimizedImage
                  src={post.coverImage}
                  alt={post.title}
                  pictureStyle={{
                    position: 'absolute',
                    inset: 0,
                    display: 'block',
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              )}
            </motion.div>

            <div className="blog-article-body">
              {sections.map((section, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5 }}
                >
                  <h2
                    ref={(el) => { sectionRefs.current[i] = el; }}
                    id={`section-${i}`}
                  >
                    {section.heading}
                  </h2>
                  <div dangerouslySetInnerHTML={{ __html: section.html }} />
                </motion.div>
              ))}
            </div>

            {tags.length > 0 && (
              <div className="blog-article-tags">
                {tags.map((tag) => (
                  <span key={tag} className="blog-article-tag">
                    <Tag size={11} style={{ marginRight: 4, display: 'inline', verticalAlign: 'middle' }} />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="blog-article-share">
              <span className="blog-article-share-label">
                <Share2 size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                Share this article
              </span>
              <a href={twitterShare} target="_blank" rel="noopener noreferrer" className="blog-article-share-btn" aria-label="Share on Twitter">
                <FaTwitter size={15} />
              </a>
              <a href={linkedinShare} target="_blank" rel="noopener noreferrer" className="blog-article-share-btn" aria-label="Share on LinkedIn">
                <FaLinkedin size={15} />
              </a>
              <button type="button" onClick={copyLink} className="blog-article-share-btn" aria-label="Copy link">
                <LinkIcon size={14} />
              </button>
            </div>

            {relatedPosts.length > 0 && (
              <div className="blog-article-related">
                <h3 className="blog-article-related-title">Related Articles</h3>
                <div className="blog-article-related-grid">
                  {relatedPosts.map((related) => (
                    <Link key={related.slug} href={`/blog/${related.slug}`} className="blog-article-related-card">
                      <span className="blog-article-category" style={{ marginBottom: 10 }}>
                        {related.category}
                      </span>
                      <h4 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
                        {related.title}
                      </h4>
                      <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{related.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* RIGHT: Author Sidebar */}
          <aside className="blog-author-col">
            <div className="author-card-sticky-wrap">
              <div className="author-card">
                <div className="author-card-cover" />
                <div className="author-card-body">
                  <div className="author-avatar-lg">
                    N
                    <span className="author-avatar-status-lg" />
                  </div>
                  <h4 className="author-name-lg">{post.author}</h4>
                  <div className="author-role-lg">
                    <Sparkles size={10} />
                    <span>Full-Stack Developer</span>
                  </div>
                  <p className="author-bio-lg">
                    Building modern web experiences with React, Next.js, and Laravel.
                    Passionate about clean code and beautiful UIs.
                  </p>

                  <div className="author-socials-lg">
                    <a href="https://github.com/nasifniloy" target="_blank" rel="noopener noreferrer" className="author-social-lg" aria-label="GitHub">
                      <FaGithub size={14} />
                    </a>
                    <a href="https://linkedin.com/in/nasifniloy" target="_blank" rel="noopener noreferrer" className="author-social-lg" aria-label="LinkedIn">
                      <FaLinkedin size={14} />
                    </a>
                    <a href="https://twitter.com/nasifniloy" target="_blank" rel="noopener noreferrer" className="author-social-lg" aria-label="Twitter">
                      <FaTwitter size={14} />
                    </a>
                    <a href="https://instagram.com/nasifniloy" target="_blank" rel="noopener noreferrer" className="author-social-lg" aria-label="Instagram">
                      <FaInstagram size={14} />
                    </a>
                  </div>

                  <Link href="/appointment" className="author-cta-lg">
                    Work with me →
                  </Link>
                </div>
              </div>

              <div className="blog-newsletter-card">
                <div className="blog-newsletter-title">Get weekly insights</div>
                <p className="blog-newsletter-sub">
                  Join my newsletter for tutorials, tips, and industry insights.
                </p>
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="blog-newsletter-input"
                />
                <button type="button" className="blog-newsletter-btn">
                  Subscribe
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
